import { Component, OnInit, DoCheck, Input, forwardRef, OnDestroy, Output, EventEmitter } from '@angular/core';
import { TemplateService } from '../../../../../services/template.service';
import { CALENDAR_LOCALE } from '../../../../../services/constant';
import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormGroup,
  NG_ASYNC_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators
} from '@angular/forms';
import { combineLatest, concat, from, Observable, of, Subject, Subscription } from 'rxjs';
import { ApiService } from '../../../../../services/api.service';
import { TranslateService } from '@ngx-translate/core';
import { catchError, debounceTime, distinctUntilChanged, filter, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../../../../environments/environment';
import { DocumentIssueFormService } from '../document-issue-form/document-issue-form.service';
import { API_CONSTANT } from '../../../../../services/api.constant';


@Component({
  selector: 'app-document-form',
  templateUrl: './document-form.component.html',
  styleUrls: ['./document-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DocumentFormComponent),
      multi: true
    },
    {
      provide: NG_ASYNC_VALIDATORS,
      useExisting: forwardRef(() => DocumentFormComponent),
      multi: true
    },
    TemplateService
  ]
})
export class DocumentFormComponent implements OnInit, DoCheck, OnDestroy, ControlValueAccessor, Validator {
  onChange: any = () => { };
  onTouched: any = () => { };

  @Input() documentType: string;
  @Input() isSubmitted: boolean;
  @Input() isDuplicateDocumentNo: boolean;
  @Output() documentRequestSelected: EventEmitter<any> = new EventEmitter;
  @Output() abbDataSelected: EventEmitter<any> = new EventEmitter();
  @Input() documentInformation: any;

  documentForm: FormGroup;
  subscription: Subscription;
  documentTypes: any[];
  documentRequests$: Observable<any>;
  documentRequestInput$ = new Subject<string>();
  searchingDocumentRequest = false;
  abbNoChecked = false;
  confirmationNoChecked = false;
  enabledCustomerRequest = environment.enabledCustomerRequest;

  abbData$: Observable<any>;
  abbNoInput$ = new Subject<string>();
  searchingAbbNo = false;

  today = new Date();
  lang: string;

  calendarLocale = CALENDAR_LOCALE.EN;
  departments = [];

  constructor(
    private apiService: ApiService,
    private formBuilder: FormBuilder,
    private template: TemplateService,
    private translateService: TranslateService,
    private documentIssueFormService: DocumentIssueFormService,
  ) { }

  ngDoCheck() {
    this.changeCalendarLocale();
  }

  ngOnInit() {
    this.changeCalendarLocale();
    this.setDocumentTypes();
    this.createDocumentForm();
    this.loadDocumentRequest();
    this.loadDepartments();
    this.loadAbbData();

    // any time the inner form changes update the parent of any change
    this.createSubscriptions();

    if (this.documentInformation) {
      this.returnDocumentDropdown(this.documentInformation);
    }
  }

  returnDocumentDropdown(documentInformation: any) {
    this.apiService.getDocumentRequestDropdown({ request_no: documentInformation.request_no }).subscribe( res => {
      this.documentRequests$ = from([res]);
    });

    if (documentInformation.abb_no) {
      this.apiService.getAbbDataDropdown({abb_no: documentInformation.abb_no}).subscribe(res => {
        this.abbData$ = from([res]);
        this.onChangeAbbNo(res[0]);
        this.loadAbbData(res);
      });
    }
  }

  get currentLang(): string {
    return this.translateService.currentLang;
  }

  loadDocumentRequest() {
    this.documentRequests$ = concat(
      of([]),
      this.documentRequestInput$.pipe(
        filter((term) => term != null && term.length >= 2),
        distinctUntilChanged(),
        debounceTime(300),
        tap(() => this.searchingDocumentRequest = true),
        switchMap((term) => {
          return this.apiService.getDocumentRequestDropdown({ request_no: term }).pipe(
            catchError(() => of([])),
            tap(() => this.searchingDocumentRequest = false),
          );
        })
      )
    );
  }

  loadAbbData(existedAbbData?: any[]) {
    this.abbData$ = concat(
      existedAbbData ? of(existedAbbData) : of([]),
      this.abbNoInput$.pipe(
        filter((term) => term != null && term.length >= 2),
        distinctUntilChanged(),
        debounceTime(300),
        tap(() => this.searchingAbbNo = true),
        switchMap((term) => {
          return this.apiService.getAbbDataDropdown({ abb_no: term }).pipe(
            catchError(() => of([])),
            tap(() => this.searchingAbbNo = false),
          )
        })
      )
    );
  }

  onChangeAbbNo(event) {
    this.abbDataSelected.emit(event);
  }

  loadDepartments(): void {
    this.apiService.getDepartments().subscribe(
      (res: any) => {
        if (res.length > 0) {
          if (this.translateService.currentLang === 'en') {
            this.departments = res.map(
              (department) => ({
                label: department.name_en,
                value: department.id,
              })
            );
          } else {
            this.departments = res.map(
              (department) => ({
                label: department.name_th,
                value: department.id,
              })
            );
          }
        }
      },
      (error: any) => {
        console.log(error);
      }
    );
  }

  changeDepartment(event): void {
    this.getRunnningInvoiceNo(event.value);
    this.documentForm.patchValue({
      department: event.value,
    });
    this.loadProducts(event.value);
  }

  onChangeDocumentRequest(event) {
    if (event && event.abb_no) {
      this.abbNoChecked = true;
      this.abbNo.enable();
    } else {
      this.abbNoChecked = false;
      this.abbNo.disable();
    }

    if (event && event.confirmation_no) {
      this.confirmationNoChecked = true;
      this.confirmationNo.enable();
    } else {
      this.confirmationNoChecked = false;
      this.confirmationNo.disable();
    }

    this.documentForm.patchValue({
      abbNo: event ? event.abb_no : '',
      confirmationNo: event ? event.confirmation_no : '',
    });
    this.documentRequestSelected.emit(event);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.documentIssueFormService.selectedProducts.next([]);
  }

  writeValue(value: any) {
    if (value) {
      this.documentForm.setValue(value);
      this.onChange(value);
      this.onTouched();
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  // communicate the inner form validation to the parent form
  validate(_control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> {
    return new Promise(resolve =>
      this.documentForm.valid ? resolve(null) : resolve({ documentForm: { valid: false } })
    );
  }

  createSubscriptions() {
    // Subscribe to valueChanges and statusChanges
    this.subscription = combineLatest([this.documentForm.valueChanges, this.documentForm.statusChanges]).subscribe(
      () => {
        this.onChange(this.documentForm.getRawValue());
        this.onTouched();
      });
  }

  setDocumentTypes() {
    this.documentTypes = this.template.getDropdown();

    if (this.isCreditNote || this.isDebitNote) {
      this.documentTypes.shift();
    } else {
      this.documentTypes = this.documentTypes.filter(element => element.value === this.documentType);
    }
  }

  changeCalendarLocale() {
    this.lang = localStorage.getItem('lang');
    this.calendarLocale = this.lang && this.lang === 'th' ? CALENDAR_LOCALE.TH : CALENDAR_LOCALE.EN;
  }

  get isCreditNote() {
    return this.documentType === 'credit_note';
  }

  get isDebitNote() {
    return this.documentType === 'debit_note';
  }

  get isDeliveryOrder() {
    return this.documentType === 'delivery_order_tax_invoice';
  }

  get isReceiptTaxInvoice() {
    return this.documentType === 'receipt_tax_invoice';
  }

  createDocumentForm() {
    const documentType = this.documentTypes.length === 1 ? this.documentTypes[0].value : null;
    const required = (this.isCreditNote || this.isDebitNote) ? Validators.required : null;

    this.documentForm = this.formBuilder.group({
      documentNo: [null, Validators.required],
      documentDate: [this.today, Validators.required],
      abbNo: [],
      confirmationNo: [{ value: null, disabled: true }],
      documentRequest: [],
      orderNo: [{ value: null, disabled: true }],
      runningNo: [{ value: null, disabled: true }],
      purpose: [null, required],
      department: [null, Validators.required],
      referenceDocuments: this.formBuilder.array([
        this.formBuilder.group({
          documentNo: [null, required],
          documentType: [documentType, required],
          documentDate: [null, required]
        })
      ])
    });
  }

  get department() {
    return this.documentForm.get('department');
  }

  get documentNo() {
    return this.documentForm.get('documentNo');
  }

  get documentDate() {
    return this.documentForm.get('documentDate');
  }

  get abbNo() {
    return this.documentForm.get('abbNo');
  }

  get confirmationNo() {
    return this.documentForm.get('confirmationNo');
  }

  get orderNo() {
    return this.documentForm.get('orderNo');
  }

  get runningNo() {
    return this.documentForm.get('runningNo');
  }

  get purpose() {
    return this.documentForm.get('purpose');
  }

  get referenceDocuments() {
    return this.documentForm.get('referenceDocuments') as FormArray;
  }

  getReferenceDocumentNo(index: number) {
    return this.referenceDocuments.controls[index].get('documentNo');
  }

  getReferenceDocumentType(index: number) {
    return this.referenceDocuments.controls[index].get('documentType');
  }

  getReferenceDocumentDate(index: number) {
    return this.referenceDocuments.controls[index].get('documentDate');
  }

  addReferenceDocument() {
    this.referenceDocuments.push(
      this.formBuilder.group({
        documentNo: [null, Validators.required],
        documentType: [null, Validators.required],
        documentDate: [null, Validators.required]
      })
    );
  }

  removeReferenceDocument(index: number) {
    this.referenceDocuments.removeAt(index);
  }

  getRunnningInvoiceNo(id: number): void {
    const params = {
      department_id: id,
    }
    this.apiService.getInvoiceNumber(params).subscribe(
      (res) => {
        if (res) {
          this.documentForm.patchValue({
            documentNo: res
          });
        }
      },
      (error) => {
        console.log(error);
      }
    );
  }

  loadProducts(id: number): void {
    const params = {
      department_id: id,
    };
    this.apiService.getProducts(params).subscribe(
      (result: any) => {
        if (result.length > 0) {
          this.documentIssueFormService.selectedProducts.next(result.map(
            (product) => ({
              product_id: product.product_id,
              product_name: `${product.name_th} (${product.name_en})`
            })
          ));
        }
      },
      (error: any) => {
        console.log(error);
      }
    );
  }
}



// WEBPACK FOOTER //
// /opt/app/src/app/features/document/document-issue/components/document-form/document-form.component.ts